import { memo } from 'react';
import {
  highlightCardContentColumnClass,
  highlightCardFeedbackClass,
  highlightCardHeaderRowClass,
  highlightCardLabelClass,
  highlightCardRootClass,
  highlightCardRootInteractiveClass,
  highlightCardUnitClass,
  highlightCardValueClass,
  highlightCardValueRowClass,
  normalizeHighlightCardState,
  normalizeHighlightCardVariant,
  resolveHighlightCardVariantStyles,
} from './highlightCardClasses.js';
import {
  formatHighlightCardValue,
  renderHighlightCardIcon,
} from './highlightCardUtils.jsx';

/**
 * Highlight Dashboard metric card (Figma DS AIMS OS — node 5029:52424).
 *
 * Layout matches Figma `Highlight Dashboard`: label + value/unit column (left),
 * {@link IconHighlight} 40×40 (right), feedback text below.
 * IconHighlight colors follow `variant`; pass `iconHighlight` to override.
 *
 * @example
 * import { Save } from 'lucide-react';
 *
 * <HighlightCard
 *   variant="green"
 *   label="Source Drives"
 *   value={342}
 *   unit="Drives"
 *   feedback="Raw documents are organized and ingested"
 *   icon={Save}
 * />
 */
const HighlightCard = memo(({
  variant = 'neutral',
  state = 'default',
  inactive = false,
  label,
  title,
  value,
  unit,
  feedback,
  description,
  showValue = true,
  showUnit = true,
  showFeedback = true,
  showIcon = true,
  icon,
  iconHighlight,
  onClick,
  className = '',
  labelClassName = '',
  valueClassName = '',
  unitClassName = '',
  feedbackClassName = '',
  ...props
}) => {
  const resolvedLabel = label ?? title;
  const resolvedFeedback = feedback ?? description;
  const normalizedVariant = normalizeHighlightCardVariant(variant);
  const normalizedState = normalizeHighlightCardState(state, inactive);
  const isInteractive = typeof onClick === 'function';
  const styles = resolveHighlightCardVariantStyles(normalizedVariant, normalizedState);

  const displayValue = formatHighlightCardValue(value, normalizedState);
  const showValueRow =
    showValue || (showUnit && unit != null && unit !== '');
  const resolvedIcon = renderHighlightCardIcon(
    icon,
    normalizedVariant,
    iconHighlight,
    normalizedState,
    showIcon,
  );

  const RootTag = isInteractive ? 'button' : 'div';

  return (
    <RootTag
      type={isInteractive ? 'button' : undefined}
      className={[
        highlightCardRootClass,
        styles.container,
        isInteractive ? highlightCardRootInteractiveClass : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-variant={normalizedVariant}
      data-state={normalizedState}
      onClick={isInteractive ? onClick : undefined}
      {...props}
    >
      <div className={highlightCardHeaderRowClass}>
        <div className={highlightCardContentColumnClass}>
          {resolvedLabel != null && resolvedLabel !== '' ? (
            <span
              className={[highlightCardLabelClass, styles.label, labelClassName]
                .filter(Boolean)
                .join(' ')}
            >
              {resolvedLabel}
            </span>
          ) : null}

          {showValueRow ? (
            <div className={highlightCardValueRowClass}>
              {showValue ? (
                <span
                  className={[highlightCardValueClass, styles.value, valueClassName]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {displayValue}
                </span>
              ) : null}
              {showUnit && unit != null && unit !== '' ? (
                <span
                  className={[highlightCardUnitClass, styles.unit, unitClassName]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {unit}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {resolvedIcon}
      </div>

      {showFeedback && resolvedFeedback != null && resolvedFeedback !== '' ? (
        <p
          className={[highlightCardFeedbackClass, styles.feedback, feedbackClassName]
            .filter(Boolean)
            .join(' ')}
        >
          {resolvedFeedback}
        </p>
      ) : null}
    </RootTag>
  );
});

export default HighlightCard;
